import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Clock, DollarSign, Truck, MapPin, XCircle, AlertTriangle, Package,
  Gavel, Zap, Tag, ChevronUp, ChevronDown, Check, X, Edit2, Trash2, ExternalLink, RefreshCw, Gift, Send, Mail
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';

const API = `${'https://shopblkmrkt.com'}/api`;

// International email domains (common non-US email providers)
const INTERNATIONAL_EMAIL_DOMAINS = [
  'naver.com', 'daum.net', 'hanmail.net', 'kakao.com', // Korea
  'qq.com', '163.com', '126.com', 'sina.com', 'sohu.com', // China
  'yahoo.co.jp', 'docomo.ne.jp', 'softbank.ne.jp', // Japan
  'mail.ru', 'yandex.ru', // Russia
  'gmx.de', 'web.de', 't-online.de', // Germany
  'orange.fr', 'free.fr', 'sfr.fr', // France
];

// Helper to check if order is from international customer
const isInternationalOrder = (order) => {
  if (order.is_international) return true;
  const email = (order.user_email || '').toLowerCase();
  return INTERNATIONAL_EMAIL_DOMAINS.some(domain => email.endsWith('@' + domain));
};

// Carrier tracking URLs
const CARRIER_TRACKING_URLS = {
  usps: 'https://tools.usps.com/go/TrackConfirmAction?tLabels=',
  ups: 'https://www.ups.com/track?tracknum=',
  fedex: 'https://www.fedex.com/fedextrack/?trknbr=',
  dhl: 'https://www.dhl.com/en/express/tracking.html?AWB='
};

export const STATUS_CONFIG = {
  pending: { icon: Clock, color: 'text-yellow-500', bg: 'bg-yellow-500/10', label: 'PENDING PAYMENT' },
  pending_fulfillment: { icon: DollarSign, color: 'text-purple-500', bg: 'bg-purple-500/10', label: 'PENDING FULFILLMENT' },
  pending_shipment: { icon: Package, color: 'text-tiffany-500', bg: 'bg-tiffany-500/10', label: 'PENDING SHIPMENT' },
  shipped: { icon: Truck, color: 'text-blue-500', bg: 'bg-blue-500/10', label: 'SHIPPED' },
  out_for_delivery: { icon: Truck, color: 'text-orange-500', bg: 'bg-orange-500/10', label: 'OUT FOR DELIVERY' },
  delivered: { icon: MapPin, color: 'text-green-500', bg: 'bg-green-500/10', label: 'DELIVERED' },
  cancelled: { icon: XCircle, color: 'text-red-500', bg: 'bg-red-500/10', label: 'CANCELLED' },
  expired: { icon: AlertTriangle, color: 'text-orange-500', bg: 'bg-orange-500/10', label: 'EXPIRED' },
  payment_plan: { icon: DollarSign, color: 'text-purple-500', bg: 'bg-purple-500/10', label: 'PAYMENT PLAN' }
};

/**
 * OrderCard - Expandable order card for admin dashboard
 * Extracted from AdminDashboard.js for maintainability
 */
export default function OrderCard({ order, onRefresh, getAuthHeader }) {
  const [expanded, setExpanded] = useState(false);
  const [status, setStatus] = useState(order.status);
  const [paypalId, setPaypalId] = useState(order.paypal_transaction_id || '');
  const [trackingNumber, setTrackingNumber] = useState(order.tracking_number || '');
  const [carrier, setCarrier] = useState(order.shipping_carrier || 'usps');
  const [method, setMethod] = useState(
    order.shipping_method || (
      order.overnight_shipping ? 'priority_express' :
      order.priority_shipping ? 'priority' : 'ground_advantage'
    )
  );
  const [adminNotes, setAdminNotes] = useState(order.admin_notes || '');
  const [showAdminNotes, setShowAdminNotes] = useState(!!order.admin_notes);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [editingId, setEditingId] = useState(false);
  const [newOrderId, setNewOrderId] = useState(order.id);
  const [sendingGiftCards, setSendingGiftCards] = useState(false);
  const [sendingReminder, setSendingReminder] = useState(false);

  // Check if order has gift cards
  const hasGiftCards = order.items?.some(item => item.is_gift_card);
  const giftCardsSent = order.gift_cards_sent;
  
  // Order is awaiting payment — show reminder button
  const awaitingPayment = ['pending', 'pending_payment', 'awaiting_payment', 'expired'].includes(order.status);
  const reminderCount = order.payment_reminder_count || 0;
  
  // Send payment reminder email
  const handleSendReminder = async () => {
    if (!window.confirm(`Send payment reminder email for ${order.id}?`)) return;
    setSendingReminder(true);
    try {
      const res = await axios.post(
        `${API}/admin/orders/${order.id}/send-payment-reminder`,
        {},
        { headers: getAuthHeader() }
      );
      toast.success(res.data.message || 'REMINDER SENT');
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'FAILED TO SEND REMINDER');
    } finally {
      setSendingReminder(false);
    }
  };

  // Send gift cards manually
  const handleSendGiftCards = async () => {
    if (!window.confirm('Send gift card code(s) to recipient? This cannot be undone.')) return;
    
    setSendingGiftCards(true);
    try {
      const res = await axios.post(
        `${API}/admin/orders/${order.id}/send-gift-cards`,
        {},
        { headers: getAuthHeader() }
      );
      toast.success(res.data.message || 'GIFT CARDS SENT');
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'FAILED TO SEND GIFT CARDS');
    } finally {
      setSendingGiftCards(false);
    }
  };

  // Determine order type
  const getOrderType = () => {
    if (order.is_auction_win) {
      return { label: 'AUCTION WIN', icon: Gavel, color: 'text-purple-400', bg: 'bg-purple-500/20' };
    }
    const hasBuyNow = order.items?.some(item => item.is_buy_now);
    if (hasBuyNow) {
      return { label: 'BUY IT NOW', icon: Zap, color: 'text-tiffany-500', bg: 'bg-tiffany-500/20' };
    }
    return { label: 'REGULAR SALE', icon: Tag, color: 'text-zinc-400', bg: 'bg-zinc-500/20' };
  };

  const orderType = getOrderType();
  const OrderTypeIcon = orderType.icon;

  // Track if there are unsaved changes
  useEffect(() => {
    const changed = 
      status !== order.status ||
      paypalId !== (order.paypal_transaction_id || '') ||
      trackingNumber !== (order.tracking_number || '') ||
      carrier !== (order.shipping_carrier || 'usps') ||
      method !== (order.shipping_method || 'ground_advantage') ||
      adminNotes !== (order.admin_notes || '');
    setHasChanges(changed);
  }, [status, paypalId, trackingNumber, carrier, method, adminNotes, order]);

  // Notify global registry whenever admin notes go dirty/clean — used to drive
  // the "SAVE ALL NOTES (N)" floating bar in AdminDashboard.
  useEffect(() => {
    const dirty = adminNotes !== (order.admin_notes || '');
    window.dispatchEvent(new CustomEvent('admin-note-dirty', {
      detail: { orderId: order.id, dirty }
    }));
    return () => {
      window.dispatchEvent(new CustomEvent('admin-note-dirty', {
        detail: { orderId: order.id, dirty: false }
      }));
    };
  }, [adminNotes, order.id, order.admin_notes]);

  // When the global bar fires SAVE ALL, save this card's notes silently if dirty.
  useEffect(() => {
    const onSaveAll = async () => {
      if (adminNotes === (order.admin_notes || '')) return;
      try {
        await axios.put(
          `${API}/admin/orders/${order.id}`,
          { admin_notes: adminNotes },
          { headers: getAuthHeader() }
        );
      } catch (e) {
        // Surface an aggregate error from the bar; ignore here
      }
    };
    window.addEventListener('admin-notes-save-all', onSaveAll);
    return () => window.removeEventListener('admin-notes-save-all', onSaveAll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminNotes, order.id, order.admin_notes]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/admin/orders/${order.id}`,
        { 
          status,
          paypal_transaction_id: paypalId || null,
          tracking_number: trackingNumber || null,
          shipping_carrier: carrier || null,
          shipping_method: method || null,
          admin_notes: adminNotes
        },
        { headers: getAuthHeader() }
      );
      toast.success('ORDER UPDATED');
      setHasChanges(false);
      onRefresh();
    } catch (error) {
      toast.error('Failed to update order');
    } finally {
      setSaving(false);
    }
  };

  const handleChangeOrderId = async () => {
    if (!newOrderId || newOrderId === order.id) {
      setEditingId(false);
      return;
    }
    try {
      await axios.put(
        `${API}/admin/orders/${order.id}/change-id`,
        { new_order_id: newOrderId },
        { headers: getAuthHeader() }
      );
      toast.success(`Order ID changed to ${newOrderId}`);
      setEditingId(false);
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change order ID');
    }
  };

  const handleDeleteOrder = async () => {
    if (!window.confirm(`Delete order ${order.id}?\n\nThis will restore stock for all items in this order.`)) {
      return;
    }
    
    setDeleting(true);
    try {
      await axios.delete(`${API}/admin/orders/${order.id}`, { headers: getAuthHeader() });
      toast.success(`Order ${order.id} deleted. Stock restored.`);
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete order');
      setDeleting(false);
    }
  };

  // Toggle the receipt status of a trade-in item attached to this order.
  // `submissionId` is required; `received` is the NEW desired state.
  const handleMarkTradeReceived = async (submissionId, received) => {
    try {
      await axios.post(
        `${API}/admin/submissions/${submissionId}/mark-received?received=${received}&silent=false`,
        null,
        { headers: getAuthHeader() }
      );
      toast.success(received ? 'TRADE ITEM RECEIVED — EMAIL SENT' : 'UNMARKED');
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update trade receipt');
    }
  };

  const statusConfig = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  const StatusIcon = statusConfig.icon;

  // Get first item name for preview
  const firstItemName = order.items?.[0]?.name || 'Unknown Item';
  const itemCount = order.items?.length || 0;

  return (
    <div 
      className={`border ${isInternationalOrder(order) ? 'border-purple-500/50 bg-purple-500/5' : 'border-zinc-800'}`} 
      data-testid={`admin-order-${order.id}`}
    >
      {/* Preview/Header - Always visible */}
      <button 
        onClick={() => setExpanded(!expanded)}
        className="w-full p-4 flex flex-wrap items-center justify-between gap-3 bg-zinc-900/30 hover:bg-zinc-900/50 transition-colors cursor-pointer text-left"
        data-testid={`order-toggle-${order.id}`}
      >
        <div className="flex items-center gap-4 flex-wrap">
          <div>
            <p className="font-mono text-xs text-zinc-500">ORDER ID</p>
            {editingId ? (
              <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                <Input
                  value={newOrderId}
                  onChange={(e) => setNewOrderId(e.target.value.toUpperCase())}
                  className="w-32 h-7 bg-zinc-800 border-zinc-700 text-tiffany-500 font-mono text-sm rounded-none"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleChangeOrderId();
                    if (e.key === 'Escape') { setEditingId(false); setNewOrderId(order.id); }
                  }}
                />
                <Button 
                  onClick={(e) => { e.stopPropagation(); handleChangeOrderId(); }}
                  className="h-7 w-7 p-0 bg-tiffany-500 hover:bg-tiffany-400 text-black"
                >
                  <Check className="w-4 h-4" />
                </Button>
                <Button 
                  onClick={(e) => { e.stopPropagation(); setEditingId(false); setNewOrderId(order.id); }}
                  className="h-7 w-7 p-0 bg-zinc-700 hover:bg-zinc-600 text-white"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <p className="font-mono text-lg text-tiffany-500 font-bold">{order.id}</p>
                <Button
                  onClick={(e) => { e.stopPropagation(); setEditingId(true); }}
                  className="h-6 w-6 p-0 bg-transparent hover:bg-zinc-700 text-zinc-500 hover:text-white"
                  title="Edit Order ID"
                >
                  <Edit2 className="w-3 h-3" />
                </Button>
              </div>
            )}
          </div>
          <div>
            <p className="font-mono text-xs text-zinc-500">DATE</p>
            <p className="font-mono text-sm text-zinc-400">
              {new Date(order.created_at).toLocaleString()}
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-4 flex-wrap">
          <div>
            <p className="font-mono text-xs text-zinc-500">CUSTOMER</p>
            <div className="flex items-center gap-2">
              <p className="font-mono text-sm text-white">{order.user_name || order.user_email || 'N/A'}</p>
              {isInternationalOrder(order) && (
                <Badge className="bg-purple-500/20 text-purple-500 rounded-none border-0 text-[10px]">
                  INTL
                </Badge>
              )}
              {order.notes && (
                <Badge className="bg-yellow-500/20 text-yellow-400 rounded-none border-0 text-[10px]">
                  CUSTOMER NOTE
                </Badge>
              )}
              {order.admin_notes && (
                <Badge className="bg-orange-500/20 text-orange-400 rounded-none border-0 text-[10px]" data-testid={`admin-note-pill-${order.id}`}>
                  ADMIN NOTE
                </Badge>
              )}
              {order.admin_created && (
                <Badge className="bg-fuchsia-500/20 text-fuchsia-400 rounded-none border-0 text-[10px]" data-testid={`admin-created-pill-${order.id}`}>
                  ADMIN CHECKOUT
                </Badge>
              )}
              {order.has_trade_item && (
                <Badge
                  className={`rounded-none border-0 text-[10px] ${order.awaiting_trade_item ? 'bg-yellow-500/20 text-yellow-400' : 'bg-tiffany-500/20 text-tiffany-400'}`}
                  data-testid={`order-trade-receipt-${order.order_id || order.id}`}
                >
                  {order.awaiting_trade_item ? 'AWAITING ITEM' : 'ITEM RECEIVED'}
                </Badge>
              )}
            </div>
          </div>
          
          <div className="text-right">
            <p className="font-mono text-xs text-zinc-500">TOTAL</p>
            <p className="font-mono text-sm text-white">${order.total.toFixed(2)}</p>
          </div>
          
          {/* Export Shipping Info Button - Shows only for pending_fulfillment */}
          {order.status === 'pending_fulfillment' && !order.tracking_number && (
            <div className="ml-auto">
              <a
                href={`${API}/admin/orders/${order.order_id || order.id}/export/pirateship`}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  const headers = getAuthHeader();
                  fetch(`${API}/admin/orders/${order.order_id || order.id}/export/pirateship`, {
                    headers
                  })
                    .then(res => {
                      if (!res.ok) throw new Error('Export failed');
                      return res.blob();
                    })
                    .then(blob => {
                      const url = window.URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `${order.order_id || order.id}.csv`;
                      a.click();
                      window.URL.revokeObjectURL(url);
                      toast.success('SHIPPING CSV EXPORTED');
                    })
                    .catch(() => toast.error('FAILED TO EXPORT'));
                }}
                className="flex items-center gap-2 bg-purple-500 text-black font-mono text-xs px-4 py-2 hover:bg-purple-400 transition-colors cursor-pointer"
                data-testid={`export-shipping-btn-${order.id}`}
              >
                <Send className="w-3 h-3" />
                EXPORT SHIPPING
              </a>
            </div>
          )}
          
          {/* Track Button - Far right side */}
          {order.tracking_number && order.status !== 'delivered' && (
            <div className="flex items-center gap-2 ml-auto">
              {order.user_email && (
                <button
                  onClick={async (e) => {
                    e.stopPropagation();
                    if (!window.confirm(`Resend tracking email to ${order.user_email}?`)) return;
                    try {
                      const response = await fetch(`${'https://shopblkmrkt.com'}/api/admin/orders/${order.order_id || order.id}/resend-tracking-email`, {
                        method: 'POST',
                        headers: getAuthHeader()
                      });
                      const data = await response.json();
                      if (response.ok) {
                        toast.success(`Tracking email sent to ${data.recipient}`);
                        onRefresh();
                      } else {
                        toast.error(data.detail || 'Failed to send tracking email');
                      }
                    } catch (error) {
                      toast.error('Failed to send tracking email');
                    }
                  }}
                  className="flex items-center gap-1 bg-zinc-700 text-tiffany-300 font-mono text-xs px-3 py-2 hover:bg-zinc-600 transition-colors"
                  data-testid={`resend-tracking-email-${order.id}`}
                  title={order.tracking_email_sent_at
                    ? `Last sent: ${new Date(order.tracking_email_sent_at).toLocaleString()}`
                    : 'No confirmed send on record — click to send'}
                >
                  <Mail className="w-3 h-3" />
                  {order.tracking_email_sent_at ? 'RESEND' : 'SEND EMAIL'}
                </button>
              )}
              <button
                onClick={async (e) => {
                  e.stopPropagation();
                  try {
                    const response = await fetch(`${'https://shopblkmrkt.com'}/api/orders/${order.order_id || order.id}/refresh-tracking`, {
                      method: 'POST',
                      headers: getAuthHeader()
                    });
                    const data = await response.json();
                    if (response.ok) {
                      toast.success(`Tracking refreshed: ${data.mapped_status?.toUpperCase()}`);
                      onRefresh();
                    } else {
                      toast.error(data.detail || 'Failed to refresh tracking');
                    }
                  } catch (error) {
                    toast.error('Failed to refresh tracking');
                  }
                }}
                className="flex items-center gap-1 bg-zinc-700 text-white font-mono text-xs px-3 py-2 hover:bg-zinc-600 transition-colors"
                data-testid={`refresh-tracking-btn-${order.id}`}
              >
                <RefreshCw className="w-3 h-3" />
                REFRESH
              </button>
              <a
                href={`${CARRIER_TRACKING_URLS[order.shipping_carrier] || CARRIER_TRACKING_URLS.usps}${order.tracking_number}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="flex items-center gap-2 bg-tiffany-500 text-black font-mono text-xs px-4 py-2 hover:bg-tiffany-400 transition-colors"
                data-testid={`track-btn-header-${order.id}`}
              >
                <ExternalLink className="w-3 h-3" />
                TRACK
              </a>
            </div>
          )}
          {/* EasyPost Status Badge - show if no track button but has status */}
          {order.easypost_status && order.status !== 'delivered' && !order.tracking_number && (
            <span className={`font-mono text-[10px] px-2 py-1 ml-auto ${
              order.easypost_status === 'delivered' ? 'bg-green-500/20 text-green-400' :
              order.easypost_status === 'in_transit' ? 'bg-blue-500/20 text-blue-400' :
              order.easypost_status === 'out_for_delivery' ? 'bg-yellow-500/20 text-yellow-400' :
              'bg-zinc-700 text-zinc-400'
            }`}>
              {order.easypost_status?.replace(/_/g, ' ').toUpperCase()}
            </span>
          )}
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {/* Order Type Badge */}
          <Badge className={`${orderType.bg} ${orderType.color} border-0 rounded-none flex items-center gap-1`}>
            <OrderTypeIcon className="w-3 h-3" />
            {orderType.label}
          </Badge>
          {/* Status Badge */}
          <Badge className={`${statusConfig.bg} ${statusConfig.color} border-0 rounded-none flex items-center gap-1`}>
            <StatusIcon className="w-3 h-3" />
            {statusConfig.label}
          </Badge>
          {/* Expand/Collapse Icon */}
          {expanded ? (
            <ChevronUp className="w-5 h-5 text-zinc-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-zinc-400" />
          )}
        </div>
      </button>

      {/* Item Preview - Always visible */}
      <div className="px-4 py-2 border-t border-zinc-800/50 bg-zinc-900/10">
        <p className="font-mono text-xs text-zinc-500">
          {itemCount} ITEM{itemCount !== 1 ? 'S' : ''}: <span className="text-zinc-400">{firstItemName}{itemCount > 1 ? ` + ${itemCount - 1} more` : ''}</span>
        </p>
      </div>

      {/* Awaiting Trade Item — always visible above admin notes when applicable */}
      {order.has_trade_item && order.awaiting_trade_item && order.status !== 'shipped' && order.status !== 'delivered' && order.status !== 'cancelled' && (
        <div
          className="px-4 py-2 border-t border-yellow-500/30 bg-yellow-500/10"
          onClick={(e) => e.stopPropagation()}
          data-testid={`awaiting-trade-${order.id}`}
        >
          <div className="flex items-center gap-3 flex-wrap">
            <span className="font-mono text-yellow-400 font-bold text-[11px] tracking-wider shrink-0">⏳ AWAITING TRADE ITEM</span>
            <span className="font-mono text-zinc-300 text-xs flex-1 min-w-[120px] truncate">
              {(order.items || []).filter(it => it.is_trade && !it.trade_received).map(it => (it.trade_item_name || 'TRADE ITEM').toUpperCase()).join(' · ')}
            </span>
            <div className="flex items-center gap-2 shrink-0">
              {(order.items || []).filter(it => it.is_trade && it.trade_submission_id && !it.trade_received).slice(0, 1).map(it => (
                <button
                  key={`btn-${it.trade_submission_id}`}
                  onClick={(e) => { e.stopPropagation(); handleMarkTradeReceived(it.trade_submission_id, true); }}
                  className="px-2 py-0.5 bg-yellow-500 text-black hover:bg-yellow-400 font-mono text-[10px] font-bold tracking-wider"
                  data-testid={`order-mark-received-${order.order_id || order.id}`}
                >
                  MARK RECEIVED
                </button>
              ))}
              {(order.items || []).filter(it => it.is_trade && it.trade_submission_id).slice(0, 1).map(it => (
                <a
                  key={it.trade_submission_id}
                  href={`/admin/submissions?q=${it.trade_submission_id}`}
                  onClick={(e) => e.stopPropagation()}
                  className="font-mono text-[10px] text-tiffany-500 hover:underline"
                >
                  {it.trade_submission_id} →
                </a>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Admin Notes - hidden by default; shows if a note exists OR user clicks ADD NOTE */}
      {showAdminNotes ? (
        <div
          className="px-4 py-3 border-t border-zinc-800/50 bg-zinc-900/20"
          onClick={(e) => e.stopPropagation()}
          data-testid={`admin-notes-inline-${order.id}`}
        >
          <div className="flex items-center justify-between mb-1.5 gap-2 flex-wrap">
            <p className="font-mono text-[10px] text-zinc-500 flex items-center gap-2">
              <span className="text-orange-400">●</span>
              ADMIN NOTES <span className="text-[9px] text-orange-500/70">PRIVATE</span>
            </p>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-1.5 cursor-pointer select-none" title="Quickly tag this order as pending payment">
                <input
                  type="checkbox"
                  checked={(adminNotes || '').toUpperCase().includes('PENDING PAYMENT')}
                  onChange={(e) => {
                    const tag = 'PENDING PAYMENT';
                    if (e.target.checked) {
                      if (!(adminNotes || '').toUpperCase().includes(tag)) {
                        setAdminNotes(adminNotes ? `${tag} — ${adminNotes}` : tag);
                      }
                    } else {
                      setAdminNotes((adminNotes || '')
                        .replace(/PENDING PAYMENT\s*[—\-:]\s*/i, '')
                        .replace(/\s*[—\-:]\s*PENDING PAYMENT/i, '')
                        .replace(/^PENDING PAYMENT\s*/i, '')
                        .trim());
                    }
                  }}
                  className="w-3.5 h-3.5 accent-orange-500"
                  data-testid={`admin-notes-pending-payment-${order.id}`}
                />
                <span className="font-mono text-[10px] text-orange-400">PENDING PAYMENT</span>
              </label>
              {order.admin_notes_updated_at && (
                <p className="font-mono text-[10px] text-zinc-600">
                  {new Date(order.admin_notes_updated_at).toLocaleString(undefined, { month:'short', day:'numeric', hour:'numeric', minute:'2-digit' })}
                </p>
              )}
              {/* Hide / clear when nothing typed yet */}
              {!adminNotes && !order.admin_notes && (
                <button
                  onClick={() => setShowAdminNotes(false)}
                  className="font-mono text-[10px] text-zinc-500 hover:text-zinc-300"
                  data-testid={`admin-notes-cancel-${order.id}`}
                >
                  CANCEL
                </button>
              )}
            </div>
          </div>
          <textarea
            value={adminNotes}
            onChange={(e) => setAdminNotes(e.target.value)}
            placeholder="Internal notes (only you see this)…"
            rows={2}
            autoFocus={!order.admin_notes}
            className="w-full bg-black/40 border border-orange-500/20 focus:border-orange-500 outline-none p-2 font-mono text-xs text-orange-200 placeholder:text-zinc-700 resize-y"
            data-testid={`admin-notes-input-${order.id}`}
          />
          {adminNotes !== (order.admin_notes || '') && (
            <div className="mt-2 flex justify-end">
              <button
                onClick={async () => {
                  await handleSave();
                  // If we just cleared the note, collapse back to the +ADD trigger
                  if (!adminNotes) setShowAdminNotes(false);
                }}
                disabled={saving}
                className="px-3 py-1.5 bg-orange-500 text-black font-mono text-[11px] font-bold hover:bg-orange-400 disabled:opacity-50"
                data-testid={`admin-notes-save-${order.id}`}
              >
                {saving ? 'SAVING…' : 'SAVE NOTES'}
              </button>
            </div>
          )}
        </div>
      ) : (
        <div
          className="px-4 py-1.5 border-t border-zinc-800/50 bg-zinc-900/10"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={() => setShowAdminNotes(true)}
            className="font-mono text-[10px] text-zinc-500 hover:text-orange-400 transition-colors"
            data-testid={`admin-notes-add-${order.id}`}
          >
            + ADD ADMIN NOTE
          </button>
        </div>
      )}
      
      {/* Expanded Details */}
      {expanded && (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x divide-zinc-800 border-t border-zinc-800">
            {/* Items */}
            <div className="p-4 space-y-2">
              <p className="font-mono text-xs text-zinc-500 mb-2">ITEMS</p>
              {order.items.map((item, idx) => (
                <div key={`${item.product_id}-${idx}`} className="space-y-1">
                  <div className="flex justify-between text-sm">
                    <span className="font-mono text-zinc-400">
                      {item.brand && item.brand !== 'None' && <span className="text-tiffany-500 text-[10px] uppercase mr-1">{item.brand}</span>}
                      {item.name}
                      {item.variant_name && <span className="text-tiffany-500 ml-1">({item.variant_name})</span>}
                      <span className="ml-1">x{item.quantity}</span>
                      {item.is_buy_now && <span className="text-tiffany-500 ml-2">(BIN)</span>}
                      {item.is_auction_win && <span className="text-purple-400 ml-2">(AUCTION)</span>}
                      {item.is_gift_card && <span className="text-amber-400 ml-2">(GIFT CARD)</span>}
                      {item.is_trade && <span className="text-purple-400 ml-2">(TRADE)</span>}
                    </span>
                    <span className="font-mono text-zinc-400">${(item.item_total || item.price || 0).toFixed(2)}</span>
                  </div>
                  {/* Trade item receipt sub-row */}
                  {item.is_trade && (
                    <div className={`ml-4 px-2 py-1.5 border text-[11px] font-mono flex items-center justify-between gap-2 ${item.trade_received ? 'bg-tiffany-500/10 border-tiffany-500/30' : 'bg-yellow-500/10 border-yellow-500/30'}`}>
                      <div className="flex items-center gap-2 min-w-0">
                        <span className={item.trade_received ? 'text-tiffany-400 font-bold' : 'text-yellow-400 font-bold'}>
                          {item.trade_received ? '✓ RECEIVED' : 'AWAITING'}
                        </span>
                        <span className="text-zinc-400 truncate">
                          {(item.trade_item_name || 'TRADE ITEM').toUpperCase()}
                        </span>
                        {item.trade_received && item.trade_received_at && (
                          <span className="text-zinc-500 shrink-0">
                            · {new Date(item.trade_received_at).toLocaleString(undefined, { month:'short', day:'numeric', hour:'numeric', minute:'2-digit' })}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {item.trade_submission_id && !item.trade_received && (
                          <button
                            onClick={(e) => { e.stopPropagation(); handleMarkTradeReceived(item.trade_submission_id, true); }}
                            className="px-2 py-0.5 bg-yellow-500 text-black hover:bg-yellow-400 font-mono text-[10px] font-bold tracking-wider"
                            data-testid={`item-mark-received-${item.trade_submission_id}`}
                          >
                            MARK RECEIVED
                          </button>
                        )}
                        {item.trade_submission_id && item.trade_received && (
                          <button
                            onClick={(e) => { e.stopPropagation(); handleMarkTradeReceived(item.trade_submission_id, false); }}
                            className="px-2 py-0.5 text-tiffany-400 hover:bg-tiffany-500/20 font-mono text-[10px] tracking-wider border border-tiffany-500/30"
                            data-testid={`item-unmark-received-${item.trade_submission_id}`}
                            title="Undo receipt"
                          >
                            UNDO
                          </button>
                        )}
                        {item.trade_submission_id && (
                          <a
                            href={`/admin/submissions?q=${item.trade_submission_id}`}
                            className="text-tiffany-500 hover:underline"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {item.trade_submission_id} →
                          </a>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
              
              {/* Gift Card Details */}
              {order.items.some(item => item.is_gift_card) && (
                <div className="mt-3 pt-3 border-t border-zinc-800">
                  <div className="flex items-center justify-between mb-2">
                    <p className="font-mono text-xs text-zinc-500">GIFT CARD DETAILS</p>
                    {giftCardsSent ? (
                      <span className="flex items-center gap-1 font-mono text-xs text-green-400">
                        <Check className="w-3 h-3" />
                        SENT
                      </span>
                    ) : (
                      <Button
                        onClick={handleSendGiftCards}
                        disabled={sendingGiftCards}
                        size="sm"
                        className="h-7 bg-amber-500 text-black hover:bg-amber-400 font-mono text-[10px] px-3 rounded-none"
                      >
                        {sendingGiftCards ? (
                          <RefreshCw className="w-3 h-3 animate-spin mr-1" />
                        ) : (
                          <Send className="w-3 h-3 mr-1" />
                        )}
                        SEND GIFT CARD
                      </Button>
                    )}
                  </div>
                  
                  {/* Auto-send note */}
                  {!giftCardsSent && (
                    <p className="font-mono text-[10px] text-zinc-600 mb-2">
                      AUTO-SENDS WHEN STATUS → "PENDING FULFILLMENT"
                    </p>
                  )}
                  
                  {order.items.filter(item => item.is_gift_card).map((item, idx) => (
                    <div key={`gc-${idx}`} className="bg-amber-500/10 border border-amber-500/30 p-3 mb-2">
                      {/* Stack vertically on mobile to prevent overlap */}
                      <div className="space-y-2">
                        <div>
                          <p className="font-mono text-[10px] text-zinc-500">RECIPIENT EMAIL</p>
                          <p className="font-mono text-white text-sm break-all">{item.gift_card_details?.recipient_email || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="font-mono text-[10px] text-zinc-500">AMOUNT</p>
                          <p className="font-mono text-amber-400 text-lg">${(item.price || 0).toFixed(2)}</p>
                        </div>
                        {item.gift_card_details?.recipient_name && (
                          <div>
                            <p className="font-mono text-[10px] text-zinc-500">RECIPIENT NAME</p>
                            <p className="font-mono text-white text-sm">{item.gift_card_details.recipient_name}</p>
                          </div>
                        )}
                        {item.gift_card_details?.message && (
                          <div>
                            <p className="font-mono text-[10px] text-zinc-500">MESSAGE</p>
                            <p className="font-mono text-zinc-400 text-xs">{item.gift_card_details.message}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  {order.gift_cards_created && order.gift_cards_created.length > 0 && (
                    <div className="mt-2 p-2 bg-green-500/10 border border-green-500/30">
                      <p className="font-mono text-[10px] text-green-400 mb-1">GIFT CARD CODES SENT:</p>
                      {order.gift_cards_created.map((gc, idx) => (
                        <p key={idx} className="font-mono text-xs text-white break-all">
                          {gc.code} - ${gc.amount} → {gc.recipient_email}
                        </p>
                      ))}
                    </div>
                  )}
                </div>
              )}
              
              {order.shipping_cost > 0 && (
                <div className="flex justify-between text-sm pt-2 border-t border-zinc-800">
                  <span className="font-mono text-zinc-500">
                    {order.overnight_shipping ? 'OVERNIGHT SHIPPING' : 'PRIORITY SHIPPING'}
                  </span>
                  <span className="font-mono text-zinc-500">${order.shipping_cost.toFixed(2)}</span>
                </div>
              )}
              
              {/* Discount Applied */}
              {order.discount_amount > 0 && (
                <div className="flex justify-between text-sm pt-2 border-t border-zinc-800">
                  <span className="font-mono text-green-500">
                    DISCOUNT {order.promo_code && `(${order.promo_code})`}
                  </span>
                  <span className="font-mono text-green-500">-${order.discount_amount.toFixed(2)}</span>
                </div>
              )}
              
              {/* Gift Card Applied */}
              {order.gift_card_amount > 0 && (
                <div className="flex justify-between text-sm pt-2 border-t border-zinc-800">
                  <span className="font-mono text-tiffany-500">
                    GIFT CARD {order.gift_card_code && `(${order.gift_card_code})`}
                  </span>
                  <span className="font-mono text-tiffany-500">-${order.gift_card_amount.toFixed(2)}</span>
                </div>
              )}

              {/* Admin Manual Discount */}
              {order.admin_manual_discount > 0 && (
                <div className="flex justify-between text-sm pt-2 border-t border-zinc-800" data-testid={`admin-manual-discount-${order.id}`}>
                  <span className="font-mono text-fuchsia-400">
                    ADMIN DISCOUNT
                  </span>
                  <span className="font-mono text-fuchsia-400">-${order.admin_manual_discount.toFixed(2)}</span>
                </div>
              )}
            </div>
            
            {/* Shipping Label Info */}
            <div className="p-4">
              <p className="font-mono text-xs text-zinc-500 mb-2">CUSTOMER EMAIL (COPY FOR PIRATE SHIP)</p>
              <div className="bg-zinc-900 border border-zinc-700 p-4 font-mono text-sm text-white select-all mb-4">
                {order.user_email || 'N/A'}
              </div>
              
              <p className="font-mono text-xs text-zinc-500 mb-2">SHIP TO (COPY FOR LABEL)</p>
              <div className="bg-zinc-900 border border-zinc-700 p-4 font-mono text-sm text-white select-all">
                <p className="font-bold">{order.shipping_name || 'N/A'}</p>
                <p>{order.shipping_address}{order.shipping_apt_unit ? `, ${order.shipping_apt_unit}` : ''}</p>
                <p>{order.shipping_city}, {order.shipping_state} {order.shipping_zip}</p>
              </div>
              
              {order.notes && (
                <div className="mt-3">
                  <p className="font-mono text-xs text-zinc-500 mb-1">CUSTOMER NOTES</p>
                  <div className="bg-yellow-500/5 border border-yellow-500/20 p-3 font-mono text-xs text-yellow-200">
                    {order.notes}
                  </div>
                </div>
              )}
            </div>
          </div>
          
          <div className="p-4 border-t border-zinc-800 space-y-4">
            {/* Status Row */}
            <div className="flex flex-wrap gap-4 items-center">
              <div>
                <label className="block font-mono text-xs text-zinc-500 mb-1">STATUS</label>
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger className="w-48 bg-zinc-900 border-zinc-800 text-white rounded-none">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800 rounded-none">
                    <SelectItem value="pending" className="text-white">PENDING PAYMENT</SelectItem>
                    <SelectItem value="pending_fulfillment" className="text-white">PENDING FULFILLMENT</SelectItem>
                    <SelectItem value="pending_shipment" className="text-white">PENDING SHIPMENT</SelectItem>
                    <SelectItem value="shipped" className="text-white">SHIPPED</SelectItem>
                    <SelectItem value="out_for_delivery" className="text-white">OUT FOR DELIVERY</SelectItem>
                    <SelectItem value="delivered" className="text-white">DELIVERED</SelectItem>
                    <SelectItem value="cancelled" className="text-white">CANCELLED</SelectItem>
                    <SelectItem value="expired" className="text-white">EXPIRED</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex-1 min-w-[200px]">
                <label className="block font-mono text-xs text-zinc-500 mb-1">PAYPAL TRANSACTION ID</label>
                <Input
                  placeholder="Enter PayPal ID..."
                  value={paypalId}
                  onChange={(e) => setPaypalId(e.target.value)}
                  className="bg-zinc-900 border-zinc-800 text-white rounded-none"
                />
              </div>
            </div>
            
            {/* Tracking Info Row */}
            <div className="flex flex-wrap gap-4 items-center">
              <div className="flex-1 min-w-[200px]">
                <label className="block font-mono text-xs text-zinc-500 mb-1">TRACKING NUMBER</label>
                <Input
                  placeholder="Enter tracking number..."
                  value={trackingNumber}
                  onChange={(e) => setTrackingNumber(e.target.value)}
                  className="bg-zinc-900 border-zinc-800 text-white rounded-none"
                />
              </div>
              
              <div>
                <label className="block font-mono text-xs text-zinc-500 mb-1">CARRIER</label>
                <Select value={carrier} onValueChange={setCarrier}>
                  <SelectTrigger className="w-32 bg-zinc-900 border-zinc-800 text-white rounded-none">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800 rounded-none">
                    <SelectItem value="usps" className="text-white">USPS</SelectItem>
                    <SelectItem value="ups" className="text-white">UPS</SelectItem>
                    <SelectItem value="fedex" className="text-white">FedEx</SelectItem>
                    <SelectItem value="dhl" className="text-white">DHL</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <label className="block font-mono text-xs text-zinc-500 mb-1">METHOD</label>
                <Select value={method} onValueChange={setMethod}>
                  <SelectTrigger className="w-40 bg-zinc-900 border-zinc-800 text-white rounded-none">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800 rounded-none">
                    <SelectItem value="ground_advantage" className="text-white">Ground Advantage</SelectItem>
                    <SelectItem value="priority" className="text-white">Priority</SelectItem>
                    <SelectItem value="priority_express" className="text-white">Priority Express (Overnight)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Save Button */}
            <div className="flex justify-between items-center pt-2 gap-2 flex-wrap">
              <Button
                onClick={handleDeleteOrder}
                disabled={deleting}
                variant="outline"
                className="rounded-none border-red-800 text-red-500 hover:bg-red-500/20"
                data-testid={`delete-order-${order.id}`}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                {deleting ? 'DELETING...' : 'DELETE ORDER'}
              </Button>
              <div className="flex items-center gap-2 ml-auto">
                {awaitingPayment && (
                  <Button
                    onClick={handleSendReminder}
                    disabled={sendingReminder}
                    variant="outline"
                    className="rounded-none border-yellow-700 text-yellow-400 hover:bg-yellow-500/10"
                    data-testid={`send-payment-reminder-${order.id}`}
                    title={reminderCount ? `Sent ${reminderCount} time(s)` : 'Send payment reminder email'}
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    {sendingReminder ? 'SENDING...' : (reminderCount > 0 ? `RESEND REMINDER (${reminderCount})` : 'SEND REMINDER')}
                  </Button>
                )}
                <Button
                  onClick={handleSave}
                  disabled={!hasChanges || saving}
                  className={`rounded-none ${hasChanges ? 'bg-tiffany-500 text-black hover:bg-tiffany-400' : 'bg-zinc-800 text-zinc-500'}`}
                >
                  {saving ? 'SAVING...' : hasChanges ? 'SAVE CHANGES' : 'NO CHANGES'}
                </Button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
